/**
 * VERONI live search — fast tile field + reliable SKU tag lookup.
 *
 * Pure 3–5 digit queries use /search (tag-aware) so SKU tags win, then
 * fall back to predictive suggest. Free-text uses suggest with short debounce.
 */
(function () {
  const component = document.querySelector('#search-modal predictive-search-component');
  const input = component?.querySelector('input[name="q"]');
  const results = component?.querySelector('[ref="predictiveSearchResults"]');
  const form = component?.querySelector('form[role="search"]');
  if (!component || !input || !results || !form) return;

  const EDGE_PUNCTUATION_RE = /^[,.;\s]+|[,.;\s]+$/g;
  const SKU_TOKEN_RE = /^\d{3,5}([A-Za-z0-9_\-]*)$/;

  function parseSkuFamilies(raw) {
    if (!raw) return null;
    const cleaned = String(raw).replace(EDGE_PUNCTUATION_RE, '').trim();
    if (!cleaned) return null;
    const tokens = cleaned.split(/[\s,]+/).filter(Boolean);
    if (!tokens.length || !tokens.every((t) => SKU_TOKEN_RE.test(t))) return null;
    return Array.from(
      new Set(tokens.map((t) => (t.match(/^(\d{3,5})/) || [])[1]).filter(Boolean))
    );
  }

  if (!form.querySelector('input[name="type"]')) {
    const typeInput = document.createElement('input');
    typeInput.type = 'hidden';
    typeInput.name = 'type';
    typeInput.value = 'product';
    form.appendChild(typeInput);
  }

  if (!input.dataset.veroniPlaceholder) {
    input.dataset.veroniPlaceholder = '1';
    input.placeholder = 'Search designs, stones, or SKU…';
  }

  let debounceTimer = 0;
  let activeController = null;
  let requestId = 0;

  function showSkeleton(count) {
    const n = count || 8;
    const tiles = Array.from({ length: n }, (_, i) => {
      return `<div class="v-tile v-tile--skeleton" style="--v-tile-i:${i}" aria-hidden="true">
        <span class="v-tile__media"></span>
        <span class="v-tile__meta">
          <span class="v-tile__skel-line"></span>
          <span class="v-tile__skel-line v-tile__skel-line--sm"></span>
        </span>
      </div>`;
    }).join('');
    results.innerHTML = `
      <div id="predictive-search-results" class="predictive-search-dropdown v-search-live">
        <div class="predictive-search-results__inner v-search-live__inner">
          <div class="v-search-live__status" role="status">Searching…</div>
          <div class="v-search-tile-grid v-search-tile-grid--skeleton">${tiles}</div>
        </div>
      </div>`;
  }

  function animateTiles(root) {
    const grid = root.querySelector(
      '[data-veroni-tile-grid], .v-search-tile-grid, .predictive-search-results__wrapper-products'
    );
    if (!grid) return;
    grid.classList.add('v-search-tile-grid');
    const cards = Array.from(
      grid.querySelectorAll(
        '.v-tile, .predictive-search-results__card--product, .predictive-search-results__card, [data-veroni-search-tile], li'
      )
    );
    cards.forEach((card, i) => {
      card.classList.add('v-tile-enter');
      card.style.setProperty('--v-tile-i', String(i));
      void card.offsetWidth;
      requestAnimationFrame(() => card.classList.add('is-in'));
    });
    // Preload first row images
    cards.slice(0, 4).forEach((card) => {
      const img = card.querySelector('img');
      if (img?.src) {
        const pre = new Image();
        pre.src = img.currentSrc || img.src;
      }
    });
  }

  function prioritizeSku(root, families) {
    const list = root.querySelector(
      '.v-search-tile-grid, .predictive-search-results__wrapper-products, .predictive-search-results__list'
    );
    if (!list) return;
    const cards = Array.from(
      list.querySelectorAll(
        '.predictive-search-results__card--product, .predictive-search-results__card, .v-tile, [data-veroni-search-tile], li'
      )
    );
    if (!cards.length) return;
    const scored = cards.map((card, index) => {
      const text = (card.textContent || '').toLowerCase();
      const href = (card.querySelector('a')?.getAttribute('href') || '').toLowerCase();
      const blob = text + ' ' + href;
      const hit = families.some((f) => blob.includes(f));
      return { card, index, hit };
    });
    scored.sort((a, b) => (a.hit === b.hit ? a.index - b.index : a.hit ? -1 : 1));
    const preferred = scored.filter((s) => s.hit);
    const keep = preferred.length ? preferred : scored.slice(0, 4);
    list.replaceChildren(...keep.map((s) => s.card));
    root.classList.add('v-search--sku');
    root.dataset.skuFamilies = families.join(',');
  }

  async function fetchSuggest(term, families, signal) {
    const q = families ? families[0] : term;
    const url = new URL('/search/suggest', window.location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', families ? '8' : '12');
    url.searchParams.set('section_id', 'predictive-search');
    const response = await fetch(url, {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(String(response.status));
    return response.text();
  }

  /** Full search page section — better at matching product tags for bare SKUs */
  async function fetchSearchPage(term, signal) {
    const url = new URL('/search', window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    // Section rendering for search results if available
    url.searchParams.set('section_id', 'veroni-search');
    const response = await fetch(url, {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(String(response.status));
    return response.text();
  }

  function buildSkuResultsFromSearchPage(html, families) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    // Prefer our tile links
    let links = Array.from(parsed.querySelectorAll('a.v-tile[href*="/products/"], a[data-veroni-search-tile]'));
    if (!links.length) {
      links = Array.from(parsed.querySelectorAll('a[href*="/products/"]')).filter((a) => {
        return a.querySelector('img') && a.closest('.v-product-grid, .v-search-tile-grid, [data-veroni-grid]');
      });
    }
    // Dedup by href
    const seen = new Set();
    const items = [];
    for (const a of links) {
      const href = a.getAttribute('href') || '';
      if (!href || seen.has(href)) continue;
      seen.add(href);
      items.push(a);
      if (items.length >= 8) break;
    }
    if (!items.length) return null;

    const grid = document.createElement('div');
    grid.className = 'v-search-tile-grid';
    grid.setAttribute('data-veroni-tile-grid', '');
    items.forEach((a, i) => {
      const wrap = document.createElement('div');
      wrap.className = 'predictive-search-results__card predictive-search-results__card--product';
      const clone = a.cloneNode(true);
      clone.classList.add('v-tile');
      clone.style.setProperty('--v-tile-i', String(i));
      wrap.appendChild(clone);
      grid.appendChild(wrap);
    });

    const root = document.createElement('div');
    root.id = 'predictive-search-results';
    root.className = 'predictive-search-dropdown v-search-live v-search--sku';
    root.dataset.skuFamilies = families.join(',');
    root.innerHTML = `
      <div class="predictive-search-results__inner v-search-live__inner">
        <h4 class="predictive-search-results__title v-search-live__label">SKU ${families.join(', ')}</h4>
      </div>`;
    root.querySelector('.predictive-search-results__inner').appendChild(grid);
    return root;
  }

  async function runSearch(term) {
    const families = parseSkuFamilies(term);
    const myId = ++requestId;
    if (activeController) activeController.abort();
    activeController = new AbortController();
    const { signal } = activeController;

    component.setAttribute('aria-busy', 'true');
    component.classList.add('is-searching');
    showSkeleton(families ? 4 : 8);

    try {
      let nextResults = null;

      if (families) {
        // SKU path: try full search first (tags + SKU text), then suggest
        try {
          const pageHtml = await fetchSearchPage(families[0], signal);
          if (myId !== requestId || input.value.trim() !== term) return;
          nextResults = buildSkuResultsFromSearchPage(pageHtml, families);
        } catch (e) {
          if (e.name === 'AbortError') throw e;
        }
        if (!nextResults) {
          try {
            const url = new URL('/search', window.location.origin);
            url.searchParams.set('q', families[0]);
            url.searchParams.set('type', 'product');
            url.searchParams.set('options[prefix]', 'last');
            const res = await fetch(url, {
              signal,
              headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
              credentials: 'same-origin',
            });
            if (res.ok) {
              const fullHtml = await res.text();
              if (myId !== requestId || input.value.trim() !== term) return;
              nextResults = buildSkuResultsFromSearchPage(fullHtml, families);
            }
          } catch (e) {
            if (e.name === 'AbortError') throw e;
          }
        }
        if (!nextResults) {
          const markup = await fetchSuggest(term, families, signal);
          if (myId !== requestId || input.value.trim() !== term) return;
          const parsed = new DOMParser().parseFromString(markup, 'text/html');
          nextResults = parsed.querySelector('#predictive-search-results');
          if (nextResults) prioritizeSku(nextResults, families);
        }
      } else {
        const markup = await fetchSuggest(term, null, signal);
        if (myId !== requestId || input.value.trim() !== term) return;
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        nextResults = parsed.querySelector('#predictive-search-results');
      }

      if (!nextResults) throw new Error('no results region');

      nextResults
        .querySelectorAll(
          '.predictive-search-results__wrapper-queries, [class*="resource-carousel"]'
        )
        .forEach((el) => el.remove());

      const productList = nextResults.querySelector(
        '.predictive-search-results__wrapper-products, .predictive-search-results__products ul, .predictive-search-results__list, .v-search-tile-grid'
      );
      if (productList) {
        productList.classList.add('v-search-tile-grid');
        productList.setAttribute('data-veroni-tile-grid', '');
      }

      const title = nextResults.querySelector('.predictive-search-results__title');
      if (title && !families) {
        title.classList.add('v-search-live__label');
        title.textContent = 'Live results';
      }

      results.replaceChildren(nextResults);
      input.setAttribute('aria-expanded', 'true');
      results.scrollTop = 0;
      requestAnimationFrame(() => animateTiles(nextResults));
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('[Veroni search]', error);
    } finally {
      if (myId === requestId) {
        component.removeAttribute('aria-busy');
        component.classList.remove('is-searching');
      }
    }
  }

  const horizonSearch = typeof component.search === 'function' ? component.search.bind(component) : null;
  if (horizonSearch) {
    component.search = function (...args) {
      if (parseSkuFamilies(input.value.trim())) return;
      return horizonSearch(...args);
    };
  }

  input.addEventListener(
    'input',
    (event) => {
    window.clearTimeout(debounceTimer);
    const term = input.value.trim();
    // Horizon's native predictive search also listens on input and
    // overwrites SKU results with empty suggest hits. Own SKU queries.
    if (parseSkuFamilies(term)) {
      event.stopImmediatePropagation();
    }
    if (!term) {
      if (activeController) activeController.abort();
      component.querySelector('[ref="resetButton"]')?.click();
      component.classList.remove('is-searching');
      return;
    }
    const families = parseSkuFamilies(term);
    debounceTimer = window.setTimeout(() => runSearch(term), families ? 30 : 60);
    },
    true
  );
})();
